import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Flags, Schema } from 'yup';
import { array, boolean, number, object, string } from 'yup';

import RecordAttachmentForm from '@components/Attachments/RecordAttachmentForm';
import type { Curation } from '@components/DrilldownHeader';
import { FormikFhirDateField, FormikTextField } from '@components/FormikFields';
import { RecordSubmitButton } from '@components/RecordSubmitButton';
import RelatedRecordsForm from '@components/RelatedRecordsTable/RelatedRecordsForm';

import * as ROUTES from '@constants/routes';

import {
  useCreateEmergencyVisit,
  useUpdateEmergencyVisit,
} from '@hooks/api/emergency-visit';
import useFhirRecordForm from '@hooks/useFhirRecordForm';
import { useFormat } from '@hooks/useFormat';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';
import { ColumnLayout, DrilldownItem } from '@layouts/DrilldownStyle';

import type {
  EncounterRecord,
  EncounterValues,
  FormValues,
} from '@models/encounter';
import { getEncounterTemplate } from '@models/fhir-templates/encounter';
import type { DrilldownProps } from '@models/record-drilldown';

import { formatFacility } from '@utils/format';
import { getRecordSourceForSubmit } from '@utils/getRecordSourceForSubmit';
import { stripEmptyStrings } from '@utils/strip';
import { dateNotBeforeSchema } from '@utils/validation';

import SharingDrilldownWrapper from '@views/Records/Common/SharingDrilldownWrapper';

import type { LocationModelInput } from 'generated/types';

import DrilldownWrapper from '../Common/DrilldownWrapper';

const messages = defineMessages({
  diagnosis: {
    id: 'emergencyVisitDrilldown.diagnosis',
    defaultMessage: 'Diagnosis',
  },
  visitDate: {
    id: 'emergencyVisitDrilldown.visitDate',
    defaultMessage: 'Visit Date',
  },
  startDate: {
    id: 'emergencyVisitDrilldown.startDate',
    defaultMessage: 'Admission Date',
  },
  endDate: {
    id: 'emergencyVisitDrilldown.endDate',
    defaultMessage: 'Discharge Date',
  },
  dischargeBeforeAdmission: {
    id: 'GLOBAL.dateValidation.dischargeBeforeAdmission',
    defaultMessage: 'Discharge date cannot be before admission date',
  },
  followUp: {
    id: 'emergencyVisitDrilldown.followUp',
    defaultMessage: 'Follow Up',
  },
  referrals: {
    id: 'emergencyVisitDrilldown.referrals',
    defaultMessage: 'Referrals',
  },
  practitioner: {
    id: 'GLOBAL.practitioner',
    defaultMessage: 'Practitioner',
  },
  facility: {
    id: 'GLOBAL.facility',
    defaultMessage: 'Facility',
  },
  city: {
    id: 'GLOBAL.city',
    defaultMessage: 'City',
  },
  country: {
    id: 'GLOBAL.country',
    defaultMessage: 'Country',
  },
  defaultTitle: {
    id: 'emergencyVisitDrilldown.defaultTitle',
    defaultMessage: 'Emergency Visit',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  visitReason: {
    id: 'emergencyVisitDrilldown.visitReason',
    defaultMessage: 'Visit Reason',
  },
  notes: {
    id: 'emergencyVisitDrilldown.notes',
    defaultMessage: 'Notes',
  },
  admission: {
    id: 'GLOBAL.admission',
    defaultMessage: 'Admission',
  },
  discharge: {
    id: 'GLOBAL.discharge',
    defaultMessage: 'Discharge',
  },
});

interface ERDrilldownProps extends DrilldownProps<EncounterRecord> {
  /**
   * When true, suppresses the default redirect to the EMERGENCY_VISITS route
   * after successfully creating a new emergency visit.
   * - Default: false (keeps existing behavior in normal routes)
   * - In auto-curation flow, this should be set to true.
   */
  disableNavigateOnCreate?: boolean;
}

const EmergencyVisit: React.FC<ERDrilldownProps> = ({
  data,
  isCreating,
  isEditing,
  isSharing,
  loading: baseLoading,
  onSubmit,
  patientId,
  isCurator,
  disableNavigateOnCreate = false,
}) => {
  const { formatMessage } = useIntl();
  const { formatDateLabel } = useFormat();
  const { enqueueSnackbar } = useSnackbar();
  const {
    formikHelpers: { baseFhirValidationSchema, initialFhirFormValues },
    loading: recordStatusAndSourceLoading,
    handleRecordFilesSourcePublishing,
  } = useFhirRecordForm();
  const isModifying = isCreating || isEditing;

  const [createERVisit, createERVisitLoading] = useCreateEmergencyVisit();
  const [updateERVisit, updateERVisitLoading] = useUpdateEmergencyVisit();

  const loading =
    baseLoading ||
    updateERVisitLoading ||
    createERVisitLoading ||
    recordStatusAndSourceLoading;

  const initialValues: FormValues = {
    ...initialFhirFormValues,
    title: data?.visitReason ?? '',
    city: data?.city ?? '',
    complications: data?.complications ?? '',
    country: data?.country ?? '',
    diagnosis: data?.diagnosis ?? '',
    endDate: data?.endDate ?? '',
    erNotes: data?.erNotes ?? '',
    facility: data?.facility ?? '',
    followUp: data?.followUp ?? '',
    practitioner: data?.practitioner ?? '',
    startDate: data?.startDate ?? '',
    visitDurationDays: data?.visitDurationDays ?? undefined,
    visitDurationUnits: data?.visitDurationUnits ?? '',
    selfReported: data?.selfReported ?? false,
    associatedResources: [],
  };

  const erValidationSchema: Schema<Partial<EncounterValues>> = object().shape({
    title: string().required(formatMessage(messages.required)),
    address: string(),
    city: string(),
    country: string(),
    complications: string(),
    diagnosis: string(),
    endDate: dateNotBeforeSchema(
      'startDate',
      formatMessage(messages.dischargeBeforeAdmission)
    ),
    erNotes: string(),
    facility: string(),
    followUp: string(),
    practitioner: string(),
    startDate: string(),
    visitDurationDays: number(),
    visitDurationUnits: string(),
    selfReported: boolean().required(formatMessage(messages.required)),
    associatedResources: array().of(
      string().required(formatMessage(messages.required))
    ),
  });

  const validationSchema: Schema<
    Partial<EncounterValues>,
    any,
    any,
    Flags
  > = erValidationSchema.concat(baseFhirValidationSchema);

  async function handleSubmit(
    values: FormValues,
    { validateForm }: FormikHelpers<FormValues>
  ) {
    if (!patientId) return;
    await validateForm(stripEmptyStrings({ ...values }));

    let er: EncounterRecord | null | undefined;
    try {
      const location: LocationModelInput | null =
        values.city || values.country || values.facility
          ? {
              name: values.facility,
              ...((values.city || values.country) && {
                address: {
                  city: values.city,
                  country: values.country,
                },
              }),
            }
          : null;

      if (data?.id) {
        const { data: updatedEr } = await updateERVisit({
          input: {
            encounter_id: data.id,
            encounter: data.getModelInput(values),
            location,
          },
        });
        er = updatedEr;
      } else {
        const { data } = await createERVisit({
          input: {
            encounter: getEncounterTemplate(patientId, values, 'emergency'),
            location,
          },
        });
        er = data;
      }
    } catch (e) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      await handleRecordFilesSourcePublishing({
        resource: er?.getResourceId(),
        files: values.files,
        toPublish: values.toPublish,
        recordSource: getRecordSourceForSubmit({
          isCurator,
          isCreating,
          initialRecordSource: initialValues.recordSource,
          currentRecordSource: values.recordSource,
        }),
      });
      // Existing behavior preserved by default.
      // In auto-curation flow, disableNavigateOnCreate=true will skip this.
      if (er && isCreating && !disableNavigateOnCreate) {
        navigate(`${ROUTES.EMERGENCY_VISITS}${er.id}`);
      }
      onSubmit?.();
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ setFieldValue, values }) => {
        const children = (
          <>
            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.diagnosis)}
                name="diagnosis"
                value={data?.diagnosis}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.followUp)}
                name="followUp"
                value={data?.followUp}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                charLimit={100000}
                label={formatMessage(messages.notes)}
                name="erNotes"
                type="text-area"
                value={data?.erNotes}
              />
            </ColumnLayout>

            <RelatedRecordsForm associations={values.associatedResources} />

            <RecordAttachmentForm
              isCreating={isCreating}
              onFileChange={(files: DroppedFile[]) => {
                setFieldValue('files', files);
              }}
              patientId={patientId}
              record={{
                attachments: data?.attachments,
                resourceId: data?.getResourceId(),
              }}
            />

            {isModifying && (
              <>
                <HorizontalRule />
                <RecordSubmitButton />
              </>
            )}
          </>
        );
        const drilldownProps: Curation.DrilldownHeaderProps = {
          items: [
            {
              editingComponent: (
                <FormikTextField
                  isHeaderItem
                  name="practitioner"
                  placeholder={formatMessage(messages.practitioner)}
                />
              ),
              title: data?.practitioner ?? 'Unknown',
              iconName: 'user-doctor',
            },
            {
              editingComponent: (
                <>
                  <FormikTextField
                    isHeaderItem
                    name="facility"
                    placeholder={formatMessage(messages.facility)}
                  />
                  <FormikTextField
                    isHeaderItem
                    name="city"
                    placeholder={formatMessage(messages.city)}
                  />
                  <FormikTextField
                    isHeaderItem
                    name="country"
                    placeholder={formatMessage(messages.country)}
                  />
                </>
              ),
              title: formatFacility(
                data?.facility,
                data?.city,
                null,
                data?.country
              ),
              iconName: 'location-dot',
            },
          ],
          loading,
          resource: data,
          selfReported: data?.selfReported,
          subtitle: isModifying ? (
            <>
              <FormikFhirDateField
                formats={['date', 'datetime', 'year']}
                label={formatMessage(messages.startDate)}
                name="startDate"
                onChange={(value) => {
                  setFieldValue('startDate', value);
                }}
                prefix={formatMessage(messages.admission)}
                value={data?.startDate}
                max={true}
              />
              <FormikFhirDateField
                formats={['date', 'datetime', 'year']}
                label={formatMessage(messages.endDate)}
                name="endDate"
                onChange={(value) => {
                  setFieldValue('endDate', value);
                }}
                prefix={formatMessage(messages.discharge)}
                value={data?.endDate}
                max={true}
                min={values.startDate}
              />
            </>
          ) : (
            formatDateLabel(
              messages.visitDate,
              data?.getVisitDateStr({ showTime: true })
            )
          ),
          title: data?.visitReason || formatMessage(messages.defaultTitle),
          titleInput: (
            <FormikTextField
              name="title"
              placeholder={formatMessage(messages.visitReason)}
            />
          ),
        };
        return (
          <Form>
            {isSharing ? (
              <SharingDrilldownWrapper {...drilldownProps}>
                {children}
              </SharingDrilldownWrapper>
            ) : (
              <DrilldownWrapper
                associatedRecords={data?.associatedRecords}
                backRoute={ROUTES.EMERGENCY_VISITS}
                notes={data?.recordNotes}
                {...drilldownProps}
              >
                {children}
              </DrilldownWrapper>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default EmergencyVisit;
